import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { flagQueryOptions } from 'providers/FlagsProvider';
import { offerLoader } from 'router/loaders/offer';
import {
  hasUserSubmittedPricingStep,
  leadHasTaxId,
} from 'router/loaders/utils/validation';
import { isVerified } from 'services/partnersSelfSignup/otp';
import { signupSearchParams } from 'router/search/signup';
import { beforeLoadSignupSubRoute, headSignupSubRoute } from 'router/utils';
import { validateLeadStatus } from 'router/validateLeadStatus';
import {
  readLeadQueryCache,
  resolveLeadQueryVariables,
} from 'services/partnersSelfSignup/signup';

export const Route = createFileRoute('/_signup/offer/')({
  validateSearch: zodValidator(signupSearchParams),
  head: headSignupSubRoute,
  beforeLoad: async ({ context, location, preload, cause }) => {
    const { queryClient, growthbook } = context;

    const queryVariables = resolveLeadQueryVariables(queryClient);
    const cacheLead = readLeadQueryCache(queryClient, queryVariables);

    const externalSourcesFeature = {
      name: 'partners.self_signup.external_sources',
      fallbackValue: false,
    };

    const data = await queryClient.ensureQueryData(
      flagQueryOptions(
        {
          ...externalSourcesFeature,
          context: {
            sessionId: cacheLead?.lead.externalSource,
          },
        },
        {
          staleTime: 0,
          enabled: !preload && Boolean(cacheLead?.lead.externalSource),
        },
      ),
    );

    if (cacheLead?.lead.externalSource && !data[externalSourcesFeature.name]) {
      throw redirect({ to: '/success', replace: true });
    }

    const updatedContext = await beforeLoadSignupSubRoute({
      context,
      location,
      preload,
      cause,
      validateLeadStatus,
      validation: {
        validate: async ({ lead }) => {
          const verified = () =>
            isVerified({ queryClient, email: lead.businessEmail ?? '' });

          if (cacheLead?.lead.offer?.offerId && (await verified())) return true;

          if (!cacheLead?.lead.externalSource) {
            const offerDecision = growthbook.isOn(
              'partners.acquisition.real-time-offer',
            );

            if (!offerDecision) {
              throw redirect({ to: '/success', replace: true });
            }
          }

          if (!hasUserSubmittedPricingStep(lead) || !(await verified())) {
            throw redirect({ to: '/pricing' });
          }

          if (!leadHasTaxId(lead)) {
            throw redirect({ to: '/gym/tax-details' });
          }

          return true;
        },
      },
    });

    if (!preload) {
      await offerLoader({
        context: { ...context, queryVariables: updatedContext.queryVariables },
      });
    }

    return updatedContext;
  },
});
